import React from 'react';
import { FormattedMessage } from 'react-intl';
import { get } from 'lodash';

import { Typography } from '@mui/material';

import { SwitchBox } from '../../molecules/switch-box';
import { ValueBox } from '../../molecules/value-box';
import { Group } from '../../organisms/group';
import { FormattedValue } from '../../store/formatted-value';
import { useSelector } from '../../store/utils';
import { ParentPaper } from '../../atoms/parent-paper';
import { FormattedWallboxStatusNext } from '../../atoms/formatted-wallbox-status-next';

export const WallboxList = () => {
  const wallboxConfig = useSelector(
    (s) => get(s.variables, 'WALLBOX_CONFIG') || {}
  );
  const wallboxKeys = useSelector((s) => {
    const config = get(s.variables, 'WALLBOX_CONFIG') || {};
    return Object.keys(config);
  });

  const wallboxIsActive = useSelector((s) => (wallboxKey: string) => {
    return Boolean(get(s.variables, `WALLBOX_CONFIG.${wallboxKey}.active`));
  });

  return (
    <Group layout="list">
      {wallboxKeys.map((wallboxKey) => (
        <ParentPaper key={wallboxKey}>
          <SwitchBox
            title={
              <FormattedMessage
                id="wallbox.active"
                defaultMessage="{name}"
                values={{
                  name:
                    (get(wallboxConfig, [wallboxKey, 'name']) as string) ?? '–',
                }}
              />
            }
            subtitle={
              wallboxIsActive(wallboxKey) ? (
                <FormattedMessage
                  id="wallbox.active.subtitle"
                  defaultMessage="Aktivní"
                />
              ) : (
                <FormattedMessage
                  id="wallbox.inactive.subtitle"
                  defaultMessage="Neaktivní"
                />
              )
            }
            switchVariableId={`WALLBOX_CONFIG.${wallboxKey}.active`}
            fullWidth
          />
          <ValueBox
            type="row"
            title={
              <FormattedMessage
                id="wallbox.status"
                defaultMessage="Režim Wallboxu"
              />
            }
            value={<FormattedWallboxStatusNext wallboxKey={wallboxKey} />}
            visibility={{
              variableId: `WALLBOX_CONFIG.${wallboxKey}.active`,
            }}
          />
          <ValueBox
            type="row"
            title={
              <FormattedMessage
                id="wallbox.common.power"
                defaultMessage="Aktuální spotřeba"
              />
            }
            value={
              <FormattedValue
                variableId={`WALLBOX_STATS.${wallboxKey}.actualPower`}
                opts={{
                  style: 'unit',
                  unit: 'kilowatt',
                  maximumFractionDigits: 1,
                  minimumFractionDigits: 1,
                }}
              />
            }
            visibility={{
              variableId: `WALLBOX_CONFIG.${wallboxKey}.active`,
            }}
            expanded={
              <Typography
                variant="body1"
                sx={{ color: 'text.disabled', mt: 1 }}
              >
                <FormattedMessage
                  id="wallbox.common.power_today"
                  defaultMessage="Dnešní spotřeba:"
                />
                &nbsp;
                <FormattedValue
                  variableId={`STATS_WB_ENERGY_CONSUMPTION_TODAY_CHARGERS.${wallboxKey}`}
                  opts={{
                    style: 'unit',
                    unit: 'kilowatt-hour',
                    maximumFractionDigits: 1,
                    minimumFractionDigits: 1,
                  }}
                />
                <br />
                <FormattedMessage
                  id="wallbox.common.power_year"
                  defaultMessage="Roční spotřeba:"
                />
                &nbsp;
                <FormattedValue
                  variableId={`STATS_WB_ENERGY_CONSUMPTION_YEAR_CHARGERS.${wallboxKey}`}
                  opts={{
                    style: 'unit',
                    unit: 'kilowatt-hour',
                    maximumFractionDigits: 1,
                    minimumFractionDigits: 1,
                  }}
                />
              </Typography>
            }
          />
          {Boolean(wallboxConfig[wallboxKey]?.rfidReaderId) && (
            <ValueBox
              type="row"
              title={
                <FormattedMessage
                  id="wallbox.authorization.status"
                  defaultMessage="Autorizace"
                />
              }
              value={<AuthorizationStatus wallboxKey={wallboxKey} />}
              visibility={{
                variableId: `WALLBOX_CONFIG.${wallboxKey}.active`,
              }}
            />
          )}
        </ParentPaper>
      ))}
    </Group>
  );
};

const AuthorizationStatus = ({ wallboxKey }: { wallboxKey: string }) => {
  const authState = useSelector((s) =>
    get(s.variables, `WALLBOX_AUTHORIZATION.${wallboxKey}`)
  );

  if (!authState) {
    return <span>-</span>;
  }

  const { status, cardName, cardUid } = authState;

  const cardInfo = cardName || cardUid || '';

  let statusMessage: React.ReactNode;
  switch (status) {
    case 'pending':
      statusMessage = (
        <FormattedMessage
          id="wallbox.authorization.status.pending"
          defaultMessage="Není autorizováno"
        />
      );
      break;
    case 'authorized':
      statusMessage = (
        <FormattedMessage
          id="wallbox.authorization.status.authorized"
          defaultMessage="Autorizováno{cardInfo}"
          values={{ cardInfo: cardInfo ? `, ${cardInfo}` : '' }}
        />
      );
      break;
    case 'denied':
      statusMessage = (
        <FormattedMessage
          id="wallbox.authorization.status.denied"
          defaultMessage="Zamítnuto{cardInfo}"
          values={{ cardInfo: cardInfo ? `, ${cardInfo}` : '' }}
        />
      );
      break;
    default:
      statusMessage = (
        <FormattedMessage
          id="wallbox.authorization.status.unknown"
          defaultMessage="Neznámý stav"
        />
      );
  }

  return <span>{statusMessage}</span>;
};
